import type { JobDetail } from "@/hooks/use-jobs";
import { StatusBadge } from "./status-badge";
import { relativeTime } from "@/lib/utils";

/**
 * Live progress panel. Shows an indeterminate bar while holding/running
 * and a deterministic bar once SSE streams progress events (M49).
 *
 * v1 MVP renders a state message; M49 hooks up real progress percentages.
 */
export function JobProgress({ job }: { job: JobDetail }) {
  const isActive =
    job.status === "pending" || job.status === "holding" || job.status === "running";

  return (
    <div className="rounded-xl border border-neutral-200 bg-white p-5 dark:border-neutral-800 dark:bg-neutral-950">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-medium">Status</h2>
        <StatusBadge status={job.status} />
      </div>

      {isActive ? (
        <>
          <div className="mt-4 h-1.5 w-full overflow-hidden rounded-full bg-neutral-200 dark:bg-neutral-800">
            <div className="h-full w-1/3 animate-pulse rounded-full bg-emerald-500" />
          </div>
          <p className="mt-3 text-xs text-neutral-500">
            {job.status === "pending"
              ? "Placing hold…"
              : job.status === "holding"
                ? "Dispatching to partner…"
                : "Partner is working on it."}
          </p>
        </>
      ) : null}

      {job.status === "completed" ? (
        <p className="mt-3 text-xs text-neutral-500">
          Completed {relativeTime(job.updated_at)}.
        </p>
      ) : null}

      {(job.status === "failed" || job.status === "cancelled") && job.error_message ? (
        <p className="mt-3 rounded-md bg-red-50 p-3 text-xs text-red-700 dark:bg-red-950 dark:text-red-300">
          <span className="font-medium">{job.error_code}:</span>{" "}
          {job.error_message}
        </p>
      ) : null}
    </div>
  );
}
